import { ActionIcon, ColorPicker, Popover, useMantineTheme } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconColorPicker } from "@tabler/icons-react";
import hexRgb from "hex-rgb";
import _ from "lodash";
import rgbHex from "rgb-hex";

import { useDocumentStyles } from "../../hooks/useDocumentStyles";
import { useCurrentEditorDocument } from "../../hooks/useEditorState";

export const CompactColorPicker = ({
  value,
  onChange,
  size = 14,
  defaultSwatchColorIndex = 1,
}: {
  value: string;
  onChange: (newColor: string) => void;
  size?: number;
  defaultSwatchColorIndex?: number;
}): JSX.Element => {
  const styles = useDocumentStyles();

  const whiteDifference = contrast(hexRgb(value), hexRgb("#fff"));
  const blackDifference = contrast(hexRgb(value), hexRgb("#000"));

  const iconOffset = whiteDifference > blackDifference ? "#fff" : "#000";

  const findColors = (o: object): string[] => {
    const entries = Object.entries(o);

    const deeperColors = entries.map(([_k, v]) => {
      if (v && typeof v === "object") {
        return findColors(v);
      } else {
        return [];
      }
    });
    const shallowColors = entries
      .filter(([k, _v]) => k.toLowerCase().includes("color"))
      .map(([_k, v]) => v);

    return _.flattenDeep([...shallowColors, ...deeperColors]);
  };

  const documentStyleColors = findColors(styles);
  const checklistElementStyleColors = useCurrentEditorDocument(
    (state) => findColors(state.contents),
    []
  );

  const uniqueDocumentColors = _.uniq(
    [...documentStyleColors, ...checklistElementStyleColors].map(
      (c) => `#${rgbHex(hexRgb(c, { format: "css" }))}`
    )
  ).filter((c) => !["#000000", "#ffffff"].includes(c));

  const theme = useMantineTheme();
  const commonSwatchPreamble = [
    "#000",
    "#fff",
    theme.colors.red[defaultSwatchColorIndex],
    theme.colors.violet[defaultSwatchColorIndex],
    theme.colors.blue[defaultSwatchColorIndex],
    theme.colors.cyan[defaultSwatchColorIndex],
    theme.colors.green[defaultSwatchColorIndex],
    theme.colors.lime[defaultSwatchColorIndex],
    theme.colors.yellow[defaultSwatchColorIndex],
    theme.colors.orange[defaultSwatchColorIndex],
  ];
  const swatchColors = [...commonSwatchPreamble, ...uniqueDocumentColors];

  const [showingColorPicker, { open, close }] = useDisclosure();
  return (
    <Popover
      trapFocus
      position="top"
      withArrow
      opened={showingColorPicker}
      onChange={(opened) => (opened ? open() : close())}
    >
      <Popover.Target>
        <ActionIcon
          onClick={() => (showingColorPicker ? close() : open())}
          variant="filled"
          size="sm"
          color={value}
          sx={(theme) => ({
            backgroundColor: value,
            border: `1px solid ${theme.colors.gray[3]}`,
            color: iconOffset,
            "&:hover": {
              backgroundColor: value,
              border: `1px solid ${theme.colors.gray[3]}`,
              color: iconOffset,
            },
          })}
        >
          <IconColorPicker size={size} />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown>
        <ColorPicker value={value} format="hex" onChangeEnd={onChange} swatches={swatchColors} />
      </Popover.Dropdown>
    </Popover>
  );
};

const RED = 0.2126;
const GREEN = 0.7152;
const BLUE = 0.0722;

const GAMMA = 2.4;

function luminance({ red, green, blue }: { red: number; green: number; blue: number }) {
  const a = [red, green, blue].map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, GAMMA);
  });
  return a[0] * RED + a[1] * GREEN + a[2] * BLUE;
}

function contrast(
  rgb1: { red: number; green: number; blue: number },
  rgb2: { red: number; green: number; blue: number }
) {
  const lum1 = luminance(rgb1);
  const lum2 = luminance(rgb2);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  return (brightest + 0.05) / (darkest + 0.05);
}
